import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';

/**
 * Copy the auxiliary auth pages (forgot password, reset password, temporary
 * login) share, merged at runtime the way the login page merges its own.
 *
 * The catalogues in `assets/i18n` are the real home for these strings. What
 * follows is a fallback for a deployed JSON that has not caught up yet, not a
 * substitute for adding the keys there: see `mergeAuthTranslations` for how
 * narrow the window is that a merge alone survives.
 */
export const AUTH_I18N_EXTRA_EN: Record<string, string> = {
  forgot_heading: 'Forgot your password?',
  forgot_lead: 'Enter the email address on your account and we will send you a link to set a new password.',
  send_reset_link: 'Send reset link',
  sending: 'Sending…',
  temporary_password: 'Temporary password',
  saving: 'Saving…',
  back_to_signin: 'Back to sign in'
};

export const AUTH_I18N_EXTRA_FR: Record<string, string> = {
  forgot_heading: 'Mot de passe oublié ?',
  forgot_lead: 'Saisissez l\'adresse e-mail de votre compte et nous vous enverrons un lien pour définir un nouveau mot de passe.',
  send_reset_link: 'Envoyer le lien de réinitialisation',
  sending: 'Envoi…',
  temporary_password: 'Mot de passe temporaire',
  saving: 'Enregistrement…',
  back_to_signin: 'Retour à la connexion'
};

const AUTH_I18N_EXTRA: Record<string, Record<string, string>> = {
  en: AUTH_I18N_EXTRA_EN,
  fr: AUTH_I18N_EXTRA_FR
};

/**
 * Top the loaded catalogues up with the copy above, and do it again whenever a
 * catalogue is (re)loaded.
 *
 * Merging once is not enough. `use()` answers immediately for the language that
 * is already current, even while its JSON is still in flight, and when that JSON
 * lands ngx-translate *replaces* the language's translations wholesale - taking
 * an earlier merge with it and leaving the view showing raw keys. Re-merging on
 * `onLangChange` puts the copy back after each such load.
 */
export function mergeAuthTranslations(translate: TranslateService, until$?: Observable<any>): void {
  // Specs construct these components with a stub translate service that only
  // answers `instant`. Nothing to merge into there.
  if (!translate?.setTranslation) {
    return;
  }

  const apply = () => {
    const loaded = translate.getLangs ? (translate.getLangs() || []) : [];
    Object.keys(AUTH_I18N_EXTRA).forEach(lang => {
      // Only top up a catalogue that is loaded, or the active one whose load is
      // already under way. Inventing an entry for a language nobody has asked
      // for yet would make ngx-translate treat it as loaded and never fetch its
      // catalogue at all - six keys of French in place of the whole file.
      if (loaded.indexOf(lang) === -1 && lang !== translate.currentLang) {
        return;
      }
      translate.setTranslation(lang, {Loginpage: AUTH_I18N_EXTRA[lang]}, true);
    });
  };

  apply();

  if (!translate.onLangChange) {
    return;
  }
  const reloads = until$
    ? translate.onLangChange.pipe(takeUntil(until$))
    : translate.onLangChange;
  reloads.subscribe(() => apply());
}
